import {
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
  useEffect
} from 'react';
import styled, { css } from 'styled-components';
import { focusStyles } from '@elfsight/widget-ui';
import { getHandleActionKeyDown } from '@elfsight/widget-utils';
import classNames from 'classnames';
import { useSlider } from '../../SliderContext';
import type { ArrowDirectionType } from '../types';
import { ArrowDirection } from '../types';

interface ArrowProps {
  sliderId: string;
  navigationClassName: string;
  customIcon: ReactNode;
  direction: ArrowDirectionType;
  onClick: () => void;
  isEnabled: boolean;
  elementRef: RefObject<HTMLDivElement>;
  oppositeRef?: RefObject<HTMLDivElement>;
  position?: string;
  withPagination?: boolean;
}

const arrowProperties = {
  [ArrowDirection.Prev]: {
    directionAffix: 'previous',
    ariaLabel: 'Previous'
  },
  [ArrowDirection.Next]: {
    directionAffix: 'next',
    ariaLabel: 'Next'
  }
};

export function Arrow({
  elementRef,
  oppositeRef,
  sliderId,
  navigationClassName,
  customIcon,
  direction,
  onClick,
  isEnabled,
  position,
  withPagination
}: ArrowProps) {
  const { appClassnamePrefix } = useSlider();
  const properties = arrowProperties[direction];

  useEffect(() => {
    const hadFocus = elementRef?.current === document.activeElement;

    if (!isEnabled && hadFocus) {
      oppositeRef?.current?.focus();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled]);

  return (
    <ArrowContainer
      ref={elementRef}
      role="button"
      tabIndex={isEnabled ? 0 : -1}
      aria-disabled={!isEnabled}
      aria-controls={sliderId}
      aria-label={properties.ariaLabel}
      onClick={onClick}
      onKeyDown={getHandleActionKeyDown(onClick)}
      _appClassPrefix={appClassnamePrefix}
      _direction={direction}
      _directionAffix={properties.directionAffix}
      _isEnabled={isEnabled}
      _navigationClass={navigationClassName}
      _position={position}
      _withPagination={withPagination}
    >
      {customIcon || (
        <DefaultArrowIcon
          appClassnamePrefix={appClassnamePrefix}
          direction={direction}
          navigationClassName={navigationClassName}
        />
      )}
    </ArrowContainer>
  );
}

interface ArrowContainerProps extends HTMLAttributes<HTMLDivElement> {
  _appClassPrefix: string;
  _navigationClass: string;
  _directionAffix: string;
  _direction: ArrowDirectionType;
  _isEnabled: boolean;
  _position?: string;
  _withPagination?: boolean;
}

const ArrowContainer = styled.div.attrs<ArrowContainerProps>(
  ({ _appClassPrefix, _isEnabled, _navigationClass, _directionAffix }) => ({
    className: classNames(
      `${_navigationClass}-arrow`,
      `${_navigationClass}-arrow-${_directionAffix}`,
      `${_appClassPrefix}-navigation-arrow`,
      `${_appClassPrefix}-navigation-${_directionAffix}`,
      {
        [`${_appClassPrefix}-navigation-enabled`]: _isEnabled
      }
    )
  })
)`
  opacity: 1;
  top: 0;
  cursor: pointer;
  z-index: 1;
  width: 36px;
  height: 36px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  transition: opacity 0.2s;
  backface-visibility: hidden;
  display: flex;

  & > * {
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);
  }

  &:focus-visible > * {
    ${focusStyles}
  }

  &:hover,
  &:focus-visible {
    opacity: 1;
    will-change: opacity;
  }

  ${({ _isEnabled }) =>
    _isEnabled
      ? 'visibility: visible; pointer-events: all;'
      : 'visibility: hidden; pointer-events: none;'}

  ${({ _position, _withPagination }) => css`
    position: ${_position ? 'relative' : 'absolute'};
    ${_position === 'bottomCenter' &&
    _withPagination &&
    'margin-top: 16px; margin-bottom: 16px;'}
  `}

  ${({ _appClassPrefix, _direction }) => css`
    ${_direction === ArrowDirection.Next ? 'right' : 'left'}: 0px;

    & .${_appClassPrefix}-arrow-default-icon {
      ${_direction === ArrowDirection.Next ? 'left' : 'right'}: 12px;
    }
  `}
`;

interface DefaultArrowIconProps {
  appClassnamePrefix: string;
  direction: ArrowDirectionType;
  navigationClassName: string;
}

function DefaultArrowIcon({
  appClassnamePrefix,
  direction,
  navigationClassName
}: DefaultArrowIconProps) {
  return (
    <DefaultArrowSVG
      viewBox="4 0 8 16"
      width="12"
      height="16"
      _appClassPrefix={appClassnamePrefix}
      _navigationClass={navigationClassName}
    >
      {direction === ArrowDirection.Prev ? (
        <path d="M4.3,8.7l6,5.9c0.4,0.4,1.1,0.4,1.5,0c0.4-0.4,0.4-1.1,0-1.5L6.5,8l5.2-5.2c0.4-0.4,0.4-1.1,0-1.5c-0.4-0.4-1.1-0.4-1.5,0l-6,6C3.9,7.7,3.9,8.3,4.3,8.7z" />
      ) : (
        <path d="M11.7,7.3l-6-5.9c-0.4-0.4-1.1-0.4-1.5,0c-0.4,0.4-0.4,1.1,0,1.5L9.5,8l-5.2,5.2c-0.4,0.4-0.4,1.1,0,1.5c0.4,0.4,1.1,0.4,1.5,0l6-6C12.1,8.3,12.1,7.7,11.7,7.3z" />
      )}
    </DefaultArrowSVG>
  );
}

interface DefaultArrowSVGProps extends HTMLAttributes<SVGElement> {
  _appClassPrefix: string;
  _navigationClass: string;
}

export const DefaultArrowSVG = styled.svg.attrs<DefaultArrowSVGProps>(
  ({ _appClassPrefix, _navigationClass }) => ({
    className: classNames(
      `${_appClassPrefix}-arrow-default-icon`,
      `${_appClassPrefix}-navigation-icon`,
      `${_navigationClass}-arrow-icon`
    )
  })
)`
  width: 12px;
  height: 16px;
  display: block;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition: 0.2s ease all;
`;
